/**
 * Detection API Service
 * Handles communication with backend detection and analysis APIs
 */

export interface DetectionSettings {
  detect_confidence: number;
  filter_min_confidence: number;
  iou_threshold: number;
}

export interface BoxCoordinates {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface DetectionResult {
  image_file: string;
  detected_objects: number;
  filtered_objects: number;
  processing_time: number;
  file_urls: {
    original: string;
    prediction: string;
    cropped_images: string[];
  };
  boxes: BoxCoordinates[];
}

export interface AnalysisTask {
  task_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  message?: string;
  total_images: number;
  processed_images: number;
  results?: any[];
}

class DetectionAPI {
  private static baseURL = import.meta.env.VITE_API_URL || 'https://web-production-df92d.up.railway.app';

  /**
   * كشف المنتجات في الصور باستخدام YOLO
   */
  static async detectProducts(
    files: File[], 
    settings: DetectionSettings = {
      detect_confidence: 0.25,
      filter_min_confidence: 0.40,
      iou_threshold: 0.45
    }
  ): Promise<DetectionResult[]> {
    const formData = new FormData();
    
    files.forEach(file => formData.append('files', file));
    formData.append('detect_confidence', settings.detect_confidence.toString());
    formData.append('filter_min_confidence', settings.filter_min_confidence.toString());
    formData.append('iou_threshold', settings.iou_threshold.toString());
    
    const response = await fetch(`${this.baseURL}/api/detect`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Detection failed: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * تحليل المنتجات المقطوعة باستخدام Gemini AI
   */
  static async analyzeBatch(
    files: File[], 
    apiKey: string,
    language: string = 'arabic'
  ): Promise<{ task_id: string; message: string }> {
    const formData = new FormData();
    
    files.forEach(file => formData.append('files', file));
    formData.append('api_key', apiKey);
    formData.append('language', language);
    
    const response = await fetch(`${this.baseURL}/api/analyze_batch`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Analysis failed: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * تتبع حالة مهمة التحليل
   */
  static async getAnalysisStatus(taskId: string): Promise<AnalysisTask> {
    const response = await fetch(`${this.baseURL}/api/analysis_status/${taskId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get analysis status: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * الحصول على نتائج التحليل
   */
  static async getAnalysisResults(taskId: string): Promise<any> {
    const response = await fetch(`${this.baseURL}/api/analysis/results/${taskId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get analysis results: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * تصدير المنتجات إلى CSV
   */
  static async exportProducts(filters: any = {}): Promise<void> {
    const params = new URLSearchParams({
      format: 'csv',
      ...filters
    });
    
    const response = await fetch(`${this.baseURL}/api/export_products?${params}`);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      throw new Error(`Export failed: ${response.statusText}`);
    }
  }

  /**
   * تصدير المنتجات إلى JSON
   */
  static async exportProductsJSON(filters: any = {}): Promise<void> {
    const params = new URLSearchParams({
      format: 'json',
      ...filters
    });
    
    const response = await fetch(`${this.baseURL}/api/export_products?${params}`);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      throw new Error(`Export failed: ${response.statusText}`);
    }
  }

  /**
   * الحصول على إحصائيات الكشف
   */
  static async getDetectionStats(): Promise<any> {
    const response = await fetch(`${this.baseURL}/api/detection/stats`);
    
    if (!response.ok) {
      throw new Error(`Failed to get detection stats: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * الحصول على معلومات النماذج المتاحة
   */
  static async getAvailableModels(): Promise<any> {
    const response = await fetch(`${this.baseURL}/api/detection/models`);
    
    if (!response.ok) {
      throw new Error(`Failed to get models info: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * حذف مهمة تحليل
   */
  static async deleteAnalysisTask(taskId: string): Promise<void> {
    const response = await fetch(`${this.baseURL}/api/analysis/tasks/${taskId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete task: ${response.statusText}`);
    }
  }

  /**
   * الحصول على جميع مهام التحليل
   */
  static async getAllAnalysisTasks(): Promise<any> {
    const response = await fetch(`${this.baseURL}/api/analysis/tasks`);
    
    if (!response.ok) {
      throw new Error(`Failed to get tasks: ${response.statusText}`);
    }
    
    return await response.json();
  }
}

export default DetectionAPI;